import * as Api from '@ViewModels';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { useErrorMessages } from '../../hooks';
import { ApiClient } from '../../services/api';
import { IPhoneCallFilterCriteria, PhoneCallFilterCriteriaProperty } from '../../viewModels/phonecall';
import { formatOptional, formatUser } from '../utils/criteriaBuilder';
import { useStatesOnFetch } from '../utils/useStatesOnFetch';

export interface IPhoneCallFilter {
	direction?: 'Inbound';
	user?: Api.IUser;
}

function filterToCriteria(filter: IPhoneCallFilter): IPhoneCallFilterCriteria[] {
	const criteria: IPhoneCallFilterCriteria[] = [];

	criteria.push(formatUser<PhoneCallFilterCriteriaProperty>(PhoneCallFilterCriteriaProperty.User, filter?.user));
	criteria.push(
		formatOptional<PhoneCallFilterCriteriaProperty>(PhoneCallFilterCriteriaProperty.Direction, filter?.direction)
	);

	return criteria.filter(item => !!item);
}

export const usePhoneCall = <T>() => {
	const { state, handleWorking, handleResponse, reset } = useStatesOnFetch<T>();

	const userSession = useUserSession();
	const errorMessages = useErrorMessages();

	const client = new ApiClient(userSession);

	const get = async (filter: IPhoneCallFilter) => {
		handleWorking();
		try {
			const response = await client.getPhoneCall(filterToCriteria(filter));
			if (!response.success) {
				throw Api.asApiError(response);
			}
			handleResponse(response);
			return response;
		} catch (error) {
			errorMessages.pushApiError(error);
		}
	};

	const getById = async (recentCallId: string) => {
		handleWorking();
		try {
			const response = await client.getPhoneCallById(recentCallId);
			if (!response.success) {
				throw Api.asApiError(response);
			}
			handleResponse(response);
			return response;
		} catch (error) {
			errorMessages.pushApiError(error);
		}
	};

	return {
		...state,
		// Ensure empty is true when data is missing or not a valid array
		empty: !state?.data || (Array.isArray(state.data) && state.data.length === 0),
		get,
		getById,
		reset,
		status: state.status,
	};
};
